import { html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './nav-flyout-mobile.css.lit.ts';
import { OutlineElement } from '../../base/outline-element/outline-element.ts';

export default class NavFlyoutMobile extends OutlineElement {
  static styles = [super.styles, componentStyles];

  static get properties() {
    return {
      // Global Navigation Menu
      globalLinks: { type: Array },
      isOpen: { type: Boolean },
      isDropdownOpen: { type: Boolean },
      // Links from main nav
      links: { type: Array },
      linkClass: { type: String },
      search: { type: Object },
      // Secondary Navigation Menu
      secondaryLinks: { type: Array },
      // Teired links from main nav
      tier1Links: { type: Array },
      tier2Links: { type: Array },
      tier3Links: { type: Array },
    };
  }

  constructor() {
    super();
    this.activeItemClass = '';
    this.globalLinks = [];
    this.isOpen = false;
    this.isDropdownOpen = false;
    this.linkClass = '';
    this.secondaryLinks = [];
    this.tier1Links = [];
    this.tier2Links = [];
    this.tier3Links = [];
  }

  firstUpdated() {
    if (this.links.length) {
      this.tier1Links = this.links.filter(link => link.url.length);
      this.tier2Links = [
        ...this.links.flatMap(link => {
          return link.sub.length ? link.sub : [];
        }),
      ];
      this.tier3Links = [
        ...this.tier2Links.flatMap(link => {
          return link.sub.length ? link.sub : [];
        }),
      ];
      this.tier2Links = this.tier2Links.filter(link => link.url.length);
      this.tier3Links = this.tier3Links.filter(link => link.url.length);
    }
  }

  menuItemOnClick(event) {
    const langToggleClass = 'nav-flyout-mobile__secondary-menu-link--language';
    if (
      event.target.classList.contains(langToggleClass) ||
      event.target.parentNode.classList.contains(langToggleClass)
    ) {
      const langModal = this.shadowRoot.getElementById('lang-modal');
      langModal.toggleModal();
      event.preventDefault();
    }
  }

  _menuModal(link, menuName) {
    // if (link.dropdownlist) {
    if (this.hasLanguageMenu(link)) {
      return html`
        <hf-icon
          class=${`nav-flyout-mobile__${menuName}-link-icon`}
          name="chevron-right"
        ></hf-icon>
      `;
    }

    return nothing;
  }

  renderLanguageModal(link, menuName) {
    return html`
      <hf-takeover-modal
        id="lang-modal"
        .isWhite="${true}"
        .forceTop="${true}"
        .noTrigger="${true}"
        .goBackArrow="${true}"
        .expandContentsOnLarge="${false}"
      >
        <div slot="modal_content">
          <ul class="nav-flyout-mobile__${menuName}-dropdown-list">
            ${Object.keys(link.dropdownlist).map(
              language => {
                return html`
                  <li
                    class="${classMap({
                      [`nav-flyout-mobile__${menuName}-dropdown-list-item`]: true,
                      [`nav-flyout-mobile__${menuName}-dropdown-list-item--active`]:
                      link.dropdownlist[language].active,
                    })}"
                  >
                    <a href="${link.dropdownlist[language].url}">
                      ${link.dropdownlist[language].text}
                    </a>
                  </li>`
              }
            )}
          </ul>
        </div>
      </hf-takeover-modal>
    `
  }

  hasLanguageMenu(link) {
    return link.dropdownlist
      && link.dropdownlist.hasOwnProperty('en')
      && link.dropdownlist['en'].isLanguage
  }

  _menuItem(link, menuName) {
    return html`
      <li class="nav-flyout-mobile__${menuName}-item">
        <a
          href=${link.url}
          target="_self"
          @click="${this.menuItemOnClick}"
          class="${classMap({
            [`nav-flyout-mobile__${menuName}-link`]: true,
            [`nav-flyout-mobile__${menuName}-link--dropdown`]: link.dropdownlist,
            [`nav-flyout-mobile__${menuName}-link--language`]: this.hasLanguageMenu(link),
            active: link.active,
          })}"
        >
          ${link.iconname
            ? html`
                <hf-icon
                  name="${link.iconname}"
                  class="nav-flyout-mobile__${menuName}--icon"
                  height="23"
                  width="23"
                ></hf-icon>
              `
            : nothing}
          <span
            class="${classMap({
              [`nav-flyout-mobile__${menuName}-link-text`]: true,
              [`nav-flyout-mobile__${menuName}-link-text--language`]:
                link.dropdownlist,
            })}"
            >${link.text}</span
          >
          ${this._menuModal(link, menuName)}
        </a>
        ${this.hasLanguageMenu(link) ? this.renderLanguageModal(link, menuName) : nothing}
      </li>
    `;
  }

  render() {
    return html`<div
      class="${classMap({
        'nav-flyout-mobile__wrapper': true,
        [`nav-flyout-mobile--open`]: this.isOpen,
        [`nav-flyout-mobile--closed`]: !this.isOpen,
      })}"
    >
      <div class="nav-flyout-mobile__header">
        <hf-link href="/" target="_self" icon>
          <div class="nav-flyout-mobile__logo-wrapper">
            <hf-icon class="nav-flyout-mobile__logo" name="hf-logo"></hf-icon>
          </div>
        </hf-link>
        <ul class="nav-flyout-mobile__global-menu-wrapper">
          ${this.globalLinks.map(link => {
            return this._menuItem(link, 'global-menu');
          })}
        </ul>
        <hf-search
          class="nav-flyout-mobile__search"
          placeholder="${this.search.text}"
          searchUrl="${this.search.url}"
        ></hf-search>
      </div>
      <div class="nav-flyout-mobile__prime-links-wrapper">
        <ul class="nav-flyout-mobile__tier2-links-wrapper">
          ${this.tier2Links.map(link => {
            return this._menuItem(link, 'tier2-links');
          })}
        </ul>
        <ul class="nav-flyout-mobile__tier1-links-wrapper">
          ${this.tier1Links.map(link => {
            return this._menuItem(link, 'tier1-links');
          })}
        </ul>
      </div>
      <div class="nav-flyout-mobile__secondary-links-wrapper">
        <ul class="nav-flyout-mobile__secondary-menu-wrapper">
          ${this.secondaryLinks.map(link => {
            return this._menuItem(link, 'secondary-menu');
          })}
        </ul>
        <ul class="nav-flyout-mobile__tier3-links-wrapper">
          ${this.tier3Links.map(link => {
            return this._menuItem(link, 'tier3-links');
          })}
        </ul>
      </div>
    </div>`;
  }
}

customElements.define('hf-nav-flyout-mobile', NavFlyoutMobile);
